import React, { MouseEvent, useCallback } from 'react';
import cn from 'classnames';
import { KeyboardShortcut, Key } from '@repo/components/tooltips';
import styles from './ItemActions.module.scss';
import {
  ActionCheckIcon,
  ActionNoteIcon,
  ContactActionIcon,
  ReminderAlarmClockIcon,
} from '@repo/icons';
import Contact from '@repo/utils/models/Contact';
import { useDispatch } from 'react-redux';
import { openModal, startNewNote, startNewReminder } from '@repo/redux/actions/ui';
import { ReconnectIcon } from '@repo/icons/Reconnect';
import { useAuthContext } from '@repo/hooks';
import { dismissActivityItem } from '@repo/redux/actions/activity';

interface ActionsProps {
  contact?: Contact;
  activityItemID?: string;
  pinToTop?: boolean;
  noBg?: boolean;
  className?: string;
  dismissPerson?: (contactID: string) => void;
  noDismissal?: boolean;
}

export const ActionIcon = ({
  handler,
  icon,
  shortcut,
  label,
  className,
}: {
  handler: (e: MouseEvent) => void;
  icon: React.ReactNode;
  shortcut: string;
  label: string;
  className?: string;
}) => (
  <KeyboardShortcut
    placement="bottom"
    shortcut={
      <>
        <Key>{shortcut}</Key>
        {label}
      </>
    }
  >
    <button onClick={handler} className={cn('btn-none', styles.actionBtn, className)} type="button">
      {icon}
    </button>
  </KeyboardShortcut>
);

export function Actions({
  activityItemID,
  contact = null,
  pinToTop,
  noBg,
  className,
  dismissPerson,
  noDismissal,
}: ActionsProps) {
  const dispatch = useDispatch();
  const { contactIsRestricted } = useAuthContext();

  // const onStar = useCallback((e: MouseEvent) => {
  //   e.stopPropagation();
  //   dispatch(toggleStarredStatus(contact.id));
  // }, []);

  const onContact = useCallback((e: MouseEvent) => {
    e.stopPropagation();
    dispatch(openModal('contactPerson'));
  }, []);

  const onReminder = useCallback((e: MouseEvent) => {
    e.stopPropagation();
    dispatch(startNewReminder);
  }, []);

  const onNote = useCallback((e: MouseEvent) => {
    e.stopPropagation();
    dispatch(startNewNote);
  }, []);

  const onSetReconnect = useCallback((e: MouseEvent) => {
    e.stopPropagation();
    dispatch(openModal('reconnect'));
  }, []);

  const onDismiss = useCallback((e: MouseEvent) => {
    if (noDismissal) return;

    e.stopPropagation();

    if (dismissPerson) {
      dismissPerson(contact.id);
      return;
    }

    dispatch(dismissActivityItem({ id: activityItemID }));
  }, []);

  return (
    <div
      className={cn(
        styles.actionsContainer,
        className,
        (!contact || pinToTop) && styles.noContact,
        noBg && styles.noBg,
      )}
    >
      {!!contact && (
        <>
          {/* <ActionIcon
            icon={<ActionStarredIcon contact={contact} />}
            handler={onStar}
            shortcut="S"
            label="Star"
          /> */}
          {!contactIsRestricted(contact) && (
            <>
              <ActionIcon
                icon={<ContactActionIcon width={20} height={20} />}
                handler={onContact}
                shortcut="C"
                label="Contact"
              />
              <ActionIcon
                icon={<ReminderAlarmClockIcon width={22} height={22} hoverOpacity />}
                handler={onReminder}
                shortcut="R"
                label="Set Reminder"
              />
              <ActionIcon
                icon={<ReconnectIcon contact={contact} />}
                handler={onSetReconnect}
                shortcut="H"
                label="Set Reconnect Cadence"
              />
              <ActionIcon
                icon={<ActionNoteIcon width={22} height={22} />}
                handler={onNote}
                shortcut="N"
                label="Add Note"
              />
            </>
          )}
        </>
      )}
      {!noDismissal && (!!activityItemID || !!dismissPerson) && (
        <ActionIcon
          icon={<ActionCheckIcon width={22} height={22} strokeWidth={2.1} hoverOpacity />}
          handler={onDismiss}
          shortcut="E"
          label="Done"
        />
      )}
    </div>
  );
}
